import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { beitreten, raumAnlegen, RaumFehler } from '../raum/api'
import { Maskottchen } from '../spiel/Maskottchen'
import { Sterne } from '../spiel/Deko'

/**
 * Beitreten und Anlegen eines Freundinnen-Raums.
 *
 * Zwei Eingabefelder, kein Konto, kein Passwort, keine Mailadresse. Damit
 * hängt an keinem Datensatz ein identifizierbares Kind, und niemand muss
 * Einwilligungen von fremden Eltern einsammeln. Der Raum-Code ist das
 * Geheimnis, und den gibt ein Erwachsener weiter.
 */
export function RaumBeitreten() {
  const [parameter] = useSearchParams()
  const navigate = useNavigate()

  const [code, setCode] = useState((parameter.get('code') ?? '').toUpperCase())
  const [spitzname, setSpitzname] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [neuerRaum, setNeuerRaum] = useState<string | null>(null)

  async function eintreten() {
    if (code.trim().length !== 6) {
      setFehler('Der Raum-Code hat sechs Zeichen.')
      return
    }
    if (!spitzname.trim()) {
      setFehler('Wie sollen dich die anderen nennen?')
      return
    }

    setLaeuft(true)
    setFehler(null)
    try {
      await beitreten(code.trim().toUpperCase(), spitzname.trim())
      navigate('/raum')
    } catch (e) {
      const grund = e instanceof RaumFehler ? e.grund : 'unbekannt'
      setFehler(fehlertext(grund))
    } finally {
      setLaeuft(false)
    }
  }

  async function anlegen() {
    setLaeuft(true)
    setFehler(null)
    try {
      const raum = await raumAnlegen('Unser Raum')
      setNeuerRaum(raum.code)
      setCode(raum.code)
    } catch {
      setFehler('Der Raum konnte nicht angelegt werden.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="buehne buehne--scroll">
      <Sterne />
      <div className="mitte">
        <Maskottchen stimmung={fehler ? 'trost' : 'staunt'} gross />
        <h1 className="titel">Gemeinsam üben</h1>
        <p className="unterzeile">
          In einem Raum fliegt ihr zusammen um die Welt und knackt jeden Tag eine Scherzfrage.
          Gib den Code ein, den du bekommen hast.
        </p>

        <input
          className="code-eingabe code-eingabe--raum"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6))}
          placeholder="ABC234"
          aria-label="Raum-Code"
          autoCapitalize="characters"
          autoCorrect="off"
        />

        <input
          className="code-eingabe code-eingabe--name"
          value={spitzname}
          onChange={(e) => setSpitzname(e.target.value.slice(0, 20))}
          placeholder="Dein Name"
          aria-label="Dein Spitzname"
        />

        {fehler && <p className="fehler">{fehler}</p>}

        {neuerRaum && (
          <p className="unterzeile" style={{ marginTop: '2vmin' }}>
            Neuer Raum angelegt. Der Code lautet <strong>{neuerRaum}</strong>. Schreib ihn dir
            auf und gib ihn den anderen Eltern weiter. Trag jetzt noch deinen Namen ein.
          </p>
        )}

        <div className="zeile" style={{ marginTop: '3vmin' }}>
          <button className="knopf" onClick={eintreten} disabled={laeuft} type="button">
            {laeuft ? 'Einen Moment …' : 'Mitmachen'}
          </button>
          <Link className="knopf knopf--leise" to="/">
            Zurück
          </Link>
        </div>

        <p className="fuss">
          Noch kein Raum da?{' '}
          <button className="alsLink" onClick={anlegen} disabled={laeuft} type="button">
            Einen neuen Raum anlegen
          </button>
        </p>
      </div>
    </div>
  )
}

function fehlertext(grund: string): string {
  if (grund === 'unbekannter-raum') return 'Diesen Raum gibt es nicht. Schau nochmal auf den Code.'
  if (grund === 'raum-voll') return 'In diesem Raum ist kein Platz mehr frei.'
  if (grund === 'zu-viele-anfragen') return 'Einen Moment noch, gleich nochmal versuchen.'
  if (grund === 'spitzname-fehlt') return 'Wie sollen dich die anderen nennen?'
  return 'Das hat nicht geklappt. Versuch es gleich nochmal.'
}
